import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Play, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import "./VideosPage.css";
import Videos_BG from "../components/ui/Videos.png";

const CATEGORIES = [
    "All",
    "Virtual Studio",
    "Virtual Production",
    "Augmented Reality",
    "Virtual Reality",
    "Showreel",
    "Projection Mapping",
];


export default function VideosPage() {

    const [videos, setVideos] = useState([]);
    const [active, setActive] = useState("All");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        setLoading(true);
        const params = active !== "All" ? { category: active } : {};
        api.get("/videos", { params })
            .then((r) => setVideos(r.data))
            .catch((e) => console.error("videos fetch", e))
            .finally(() => setLoading(false));
    }, [active]);


    useEffect(() => {
        if (!selected) return;
        const onKey = (e) => e.key === "Escape" && setSelected(null);
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [selected]);

    return (
        <main data-testid="videos-page" className="videos-page-wrapper">
            {/* Hero */}
            <section className="videos-hero border-bottom position-relative overflow-hidden">
                <div
                    className="hero-bg-image"
                    style={{ backgroundImage: `url(${Videos_BG})` }}
                    aria-hidden
                />
                <div
                    className="hero-overlay"
                    aria-hidden
                />
                <div className="grain position-absolute top-0 bottom-0 start-0 end-0 pe-none" aria-hidden />

                <div className="container-db position-relative">
                    <div className="font-mono-tech section-eyebrow fade-in-up fs-5">
                        ⟶ SHOWREEL
                    </div>
                    <h1
                        data-testid="videos-page-title"
                        className="font-heading hero-title mb-0"
                    >
                        Video
                        <br />
                        <span className="text-primary-custom">Gallery</span>
                    </h1>
                    <p className="videos-hero-desc mt-4 mb-0" style={{ fontSize: "20px" }}>
                        Explore our portfolio of showreels, defence simulations, virtual production case studies, immersive experiences, projection mapping, interactive technologies, and broadcast solutions - showcasing innovation in action.
                    </p>
                </div>
            </section>

            {/* Filters */}
            <section className="sticky-filters border-bottom py-4">
                <div className="container-db d-flex flex-wrap gap-2">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c} style={{ fontSize: '12px', color: "#f1eaeaff" }}
                            onClick={() => setActive(c)}
                            data-testid={`videos-filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`interest-btn font-mono-tech ${active === c ? "active" : ""
                                }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section className="py-5 py-md-10">
                <div className="container-db">
                    {loading ? (
                        <div className="row g-3">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="col-12 col-md-6 col-lg-4">
                                    <div className="video-pulse-card" />
                                </div>
                            ))}
                        </div>
                    ) : videos.length === 0 ? (
                        <div
                            data-testid="videos-empty"
                            className="text-center py-5 text-muted-custom font-mono-tech text-xs uppercase tracking-[0.2em]"
                        >
                            No videos in this category yet.
                        </div>
                    ) : (
                        <div className="row g-3">
                            {videos.map((v) => (
                                <div key={v.id} className="col-12 col-md-6 col-lg-4 d-flex">
                                    <VideoCard video={v} onPlay={() => setSelected(v)} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-5 py-md-10 border-top border-secondary">
                <div className="container-db d-flex flex-column align-items-center justify-content-center text-center gap-4">
                    <h3 className="font-heading section-heading mb-0">
                        Inspired by What You See?
                    </h3>
                    <Link
                        to="/contact"
                        data-testid="videos-cta-contact"
                        className="btn-submit font-mono-tech text-decoration-none d-inline-flex align-items-center"
                        style={{ width: 'auto' }}
                    >
                        Start a Project  <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>

            {/* Lightbox */}
            {selected && (
                <div
                    data-testid="video-lightbox"
                    className="position-fixed top-0 bottom-0 start-0 end-0 lightbox-overlay d-flex align-items-center justify-content-center p-4 p-md-5"
                    onClick={() => setSelected(null)}
                >
                    <button
                        onClick={() => setSelected(null)}
                        data-testid="video-lightbox-close"
                        aria-label="Close video"
                        className="position-absolute top-4 end-4 top-md-5 end-md-5 btn-lightbox-close"
                    >
                        <X size={28} />
                    </button>
                    <div
                        className="w-100 max-w-6xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="aspect-video w-100 bg-black border border-secondary">
                            <iframe
                                title={selected.title}
                                src={`https://www.youtube.com/embed/${selected.id}?autoplay=1&rel=0`}
                                className="w-100 h-100"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                data-testid="video-lightbox-iframe"
                            />
                        </div>
                        <div className="mt-4 d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
                            <div>
                                <div className="font-mono-tech lightbox-category mb-2">
                                    {selected.category}
                                </div>
                                <h2 className="font-heading lightbox-title mb-0">
                                    {selected.title}
                                </h2>
                                {selected.description && (
                                    <p className="text-muted-custom mt-3 mb-0 max-w-2xl">{selected.description}</p>
                                )}
                            </div>
                            <a
                                href={`https://www.youtube.com/watch?v=${selected.id}`}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="video-lightbox-youtube"
                                className="immersive-link font-mono-tech text-decoration-none align-self-start"
                            >
                                Watch on YouTube <ArrowUpRight size={16} />
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}



const VideoCard = ({ video, onPlay }) => {

    const thumb = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
    return (
        <button
            type="button"
            onClick={onPlay}
            data-testid={`video-card-${video.id}`}
            className="position-relative w-100 h-100 overflow-hidden group-hover-parent video-card text-start p-0 d-flex flex-column justify-content-between"
        >
            <div className="position-relative aspect-video overflow-hidden w-100">
                <img
                    src={thumb}
                    alt={video.title}
                    loading="lazy"
                    className="absolute-img bg-cover bg-center"
                />
                <div className="video-card-overlay" />
                <div className="position-absolute inset-0 d-flex align-items-center justify-content-center">
                    <span className="play-btn-card d-flex align-items-center justify-content-center">
                        <Play size={24} fill="currentColor" strokeWidth={0} className="ms-1" />
                    </span>
                </div>
            </div>
            <div className="p-3 bg-dark w-100 flex-grow-1 d-flex align-items-center">
                <h4 className="font-heading video-card-title m-0" style={{ fontSize: '15px', color: "#40e0d0" }}>
                    {video.title}
                </h4>
            </div>
        </button>
    );
};
